import React, { useState, useEffect, useRef } from 'react';
import { fetchDestinations, fetchProperties } from '../../api-client'; // Adjust paths as necessary
import { Property } from '../../../../backend/src/models/getProperty'; // Adjust path as necessary
import { HotelDestination } from '../../../../backend/src/models/getDestination';

interface CountryDropdownProps {
  onHotelSelect: (hotel: Property, destinationCode: string) => void; // Callback function to pass selected hotel data
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({ onHotelSelect }) => {
  const [countries, setCountries] = useState<HotelDestination[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>(''); // Initialize with an empty string
  const [filteredCountries, setFilteredCountries] = useState<HotelDestination[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<HotelDestination | null>(null);
  const [hotels, setHotels] = useState<Property[]>([]); // This will hold the hotel list
  const [filteredHotels, setFilteredHotels] = useState<Property[]>([]);
  const [searchHotelQuery, setSearchHotelQuery] = useState<string>(''); // Initialize with an empty string
  const [loading, setLoading] = useState<boolean>(false); // Loading state for hotels
  const [destinationCode, setDestinationCode] = useState<string>('');

  const inputRef = useRef<HTMLInputElement>(null);
  const hotelInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchDestinationsData = async () => {
      try {
        const data = await fetchDestinations();
        setCountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchDestinationsData();
  }, []);

  
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCountries([]); 
    } else {
      const filtered = countries.filter(country =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery, countries]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value || ''); // Ensure the value is always a string
    setFilteredCountries([]); // Hide dropdown during search
    setSelectedCountry(null); // Reset the selected country if user starts typing
    setHotels([]); // Reset the hotels list
    setFilteredHotels([]); // Hide hotel dropdown during search
  };

  const handleCountrySelect = async (country: HotelDestination) => {
    setSelectedCountry(country);
    setSearchQuery(country.name); // Use `name` instead of `description.content`
    
    setTimeout(() => setFilteredCountries([]), 0);

    if (inputRef.current) {
      setTimeout(() => inputRef.current?.blur(), 0);
    }

    setLoading(true);
    try {
      if (selectedCountry) {
      }
      const hotelsData = await fetchProperties(country.code); // Use `country.code`
      setHotels(hotelsData);
      setDestinationCode(country.code);
    } catch (error) {
      console.error('Error fetching hotels:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchHotelQuery.trim() === '') {
      setFilteredHotels([]);
    } else {
      const filtered = hotels.filter(hotel =>
        hotel.name.toLowerCase().includes(searchHotelQuery.toLowerCase())
      );
      setFilteredHotels(filtered);
    }
  }, [searchHotelQuery, hotels]);

  const handleHotelSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchHotelQuery(event.target.value || ''); 
    setFilteredHotels([]); 
  };

  const handleHotelSelect = (hotel: Property) => {
    setSearchHotelQuery(hotel.name); 

    setTimeout(() => setFilteredHotels([]), 0);

    if (hotelInputRef.current) {
      setTimeout(() => hotelInputRef.current?.blur(), 0);
    }

    onHotelSelect(hotel, destinationCode);
  };

  return (
    <div className="country-dropdown relative">
      <label htmlFor="country-search" className="text-gray-700 text-sm font-bold">
        Select Country
      </label>
      <input
        type="text"
        id="country-search"
        value={searchQuery} 
        onChange={handleSearchChange}
        placeholder="Search country..."
        ref={inputRef} 
        className="border rounded w-full py-2 px-3 mt-1 text-gray-700 focus:outline-none focus:border-pink-400"
        autoComplete="off"
      />
      {filteredCountries.length > 0 && searchQuery.trim() !== '' && (
        <ul
          className="border rounded mt-1 max-h-60 overflow-y-auto absolute w-full bg-white z-10"
        >
          {filteredCountries.map((country, index) => (
            <li
              key={index}
              onMouseDown={(e) => e.preventDefault()} 
              onClick={() => handleCountrySelect(country)} 
              className="cursor-pointer px-3 py-2 hover:bg-pink-200"
            >
              {country.name} {/* Display country name */}
            </li>
          ))}
        </ul>
      )}

      {loading && <div className='text-halo-pink mt-4'>Loading hotels...</div>}
      {!loading && hotels.length > 0 && (
        <div className="mt-4">
          <label htmlFor="hotel-search" className="text-gray-700 text-sm font-bold">
            Select Hotel
          </label>
          <input
            type="text"
            id="hotel-search"
            value={searchHotelQuery} 
            onChange={handleHotelSearchChange}
            placeholder="Search hotel..."
            ref={hotelInputRef} 
            className="border rounded w-full py-2 px-3 mt-1 text-gray-700 focus:outline-none focus:border-pink-400"
            autoComplete="off"
          />
          {filteredHotels.length > 0 && searchHotelQuery.trim() !== '' && (
            <ul
              className="border rounded mt-1 max-h-60 overflow-y-auto absolute w-full bg-white z-10"
            >
              {filteredHotels.map((hotel, index) => (
                <li
                  key={index}
                  onMouseDown={(e) => e.preventDefault()} 
                  onClick={() => handleHotelSelect(hotel)}
                  className="cursor-pointer px-3 py-2 hover:bg-pink-200"
                >
                  {hotel.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CountryDropdown;
